import * as React from 'react';

/** What is left of `TData` once the gate below has rejected every value it treats as absent. */
type Present<TData> = Exclude<NonNullable<TData>, false>;

type Props<TData> = {
	children: ((data: Present<TData>) => React.ReactNode) | React.ReactNode;
	data: TData;
	fallback?: React.ReactNode;
};

/**
 * Renders its children once `data` is present, handing them that value already narrowed to
 * `Present<TData>`; renders `fallback` while it is absent.
 *
 * `children` is a function, not a node: a node is built and type-checked in the caller's scope before
 * this component ever runs, so no signature could narrow anything there. Deferring the build is what
 * makes the narrowing reachable, and it spares the caller the `?.` and `?? []` fallbacks it would
 * otherwise write to satisfy a branch that never renders. `fallback` stays a node — it receives
 * nothing, so there is nothing to defer.
 *
 * Absent means `null`, `undefined` or `false` — never `0` or `''`, which render. The cut is drawn
 * there because `false` is the one falsy value the type system can subtract faithfully (`boolean` is
 * the union `true | false`), whereas `Exclude<number, 0>` is still `number`: gating on `0` would hide
 * a `data={items.length}` at zero while the signature kept promising a `number`. A caller who wants a
 * count to gate writes the comparison, `data={items.length > 0}`, and gets both the behaviour and the
 * type.
 *
 * `fallback` answers "nothing to show", never "not loaded yet": one node cannot stand for both, and a
 * screen that owes its reader the difference — a pending read is not an empty result — carries a
 * discriminated union and branches on it, as the processing screens do.
 *
 * Being generic, this component cannot be declared `React.FC`, which takes no free type parameter.
 */
const ConditionalLoader = <TData,>({ children, data, fallback = null }: Props<TData>): React.ReactNode => {
	if (data === null || data === undefined || data === false) {
		return <React.Fragment>{fallback}</React.Fragment>;
	}

	if (typeof children === 'function') {
		// No check narrows an unconstrained type parameter, hence the assertion — the branch above is what
		// makes it sound, and it stays confined to this line.
		return <React.Fragment>{children(data as Present<TData>)}</React.Fragment>;
	}

	return <React.Fragment>{children}</React.Fragment>;
};

export default ConditionalLoader;
